/**
 * Studies React Query Hooks
 *
 * TanStack Query hooks for study management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  StudySearchFilters,
  CreateStudyData,
  UpdateStudyData,
  StudyStatus,
  AmendStudyData,
} from '@/domain/study/types'
import { useAuth } from '@/application/auth'
import * as studyService from './study.service'

// =============================================================================
// Query Keys
// =============================================================================

export const studyKeys = {
  all: ['studies'] as const,
  lists: () => [...studyKeys.all, 'list'] as const,
  list: (filters: StudySearchFilters) => [...studyKeys.lists(), filters] as const,
  details: () => [...studyKeys.all, 'detail'] as const,
  detail: (id: string) => [...studyKeys.details(), id] as const,
  patientStudies: (patientId: string) => [...studyKeys.all, 'patient', patientId] as const,
  download: (id: string) => [...studyKeys.all, 'download', id] as const,
  amendInfo: (id: string) => [...studyKeys.all, 'amend-info', id] as const,
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch paginated studies list
 */
export function useStudies(filters?: StudySearchFilters) {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: studyKeys.list(filters ?? {}),
    queryFn: () => studyService.getStudies(filters),
    enabled: isAuthenticated,
  })
}

/**
 * Hook to fetch a single study by ID
 */
export function useStudy(id: string | undefined) {
  return useQuery({
    queryKey: studyKeys.detail(id ?? ''),
    queryFn: async () => {
      const response = await studyService.getStudyById(id!)
      return response.study
    },
    enabled: !!id,
  })
}

/**
 * Hook to fetch studies for a specific patient
 */
export function usePatientStudies(
  patientId: string | undefined,
  filters?: Omit<StudySearchFilters, 'patientId'>
) {
  return useQuery({
    queryKey: studyKeys.patientStudies(patientId ?? ''),
    queryFn: () => studyService.getPatientStudies(patientId!, filters),
    enabled: !!patientId,
  })
}

/**
 * Descarga el estudio completo.
 *
 * Es una mutacion y no una consulta: descargar es una accion que el usuario
 * dispara, no un dato que la pantalla necesita. Como consulta, ademas, se
 * pedia el paquete entero al abrir el detalle del estudio, lo usara o no.
 */
export function useDescargarEstudio() {
  return useMutation({
    mutationFn: (id: string) => studyService.descargarEstudio(id),
    onSuccess: ({ blob, nombre }) => guardarArchivo(blob, nombre),
  })
}

/** Le entrega el archivo al navegador. */
function guardarArchivo(blob: Blob, nombre: string): void {
  const url = window.URL.createObjectURL(blob)
  const enlace = document.createElement('a')
  enlace.href = url
  enlace.download = nombre
  document.body.appendChild(enlace)
  enlace.click()
  document.body.removeChild(enlace)
  window.URL.revokeObjectURL(url)
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to upload a new study (single file)
 */
export function useUploadStudy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateStudyData) => studyService.uploadStudy(data),
    onSuccess: (result) => {
      // Invalidate study lists
      queryClient.invalidateQueries({ queryKey: studyKeys.lists() })
      // Invalidate patient studies if we know the patient
      if (result.study.patientId) {
        queryClient.invalidateQueries({
          queryKey: studyKeys.patientStudies(result.study.patientId),
        })
      }
    },
  })
}

/**
 * Hook to upload a new study with multiple DICOM files (batch)
 */
export function useUploadStudyBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateStudyData) => studyService.uploadStudyBatch(data),
    onSuccess: (result) => {
      // Invalidate study lists
      queryClient.invalidateQueries({ queryKey: studyKeys.lists() })
      // Invalidate patient studies if we know the patient
      if (result.study.patientId) {
        queryClient.invalidateQueries({
          queryKey: studyKeys.patientStudies(result.study.patientId),
        })
      }
    },
  })
}

/**
 * Hook to update study metadata
 */
export function useUpdateStudy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStudyData }) =>
      studyService.updateStudy(id, data),
    onSuccess: (result, variables) => {
      // Update the study in cache
      queryClient.setQueryData(studyKeys.detail(variables.id), result.study)
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: studyKeys.lists() })
    },
  })
}

/**
 * Hook to update study status
 */
export function useUpdateStudyStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: StudyStatus }) =>
      studyService.updateStudyStatus(id, status),
    onSuccess: (result, variables) => {
      // Update the study in cache
      queryClient.setQueryData(studyKeys.detail(variables.id), result.study)
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: studyKeys.lists() })
    },
  })
}

/**
 * Hook to delete a study
 */
export function useDeleteStudy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => studyService.deleteStudy(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: studyKeys.detail(deletedId) })
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: studyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: studyKeys.all })
    },
  })
}

/**
 * Hook to fetch study amend info (for returned studies)
 */
export function useStudyAmendInfo(id: string | undefined) {
  return useQuery({
    queryKey: studyKeys.amendInfo(id ?? ''),
    queryFn: () => studyService.getStudyAmendInfo(id!),
    enabled: !!id,
  })
}

/**
 * Hook to amend a study
 */
export function useAmendStudy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AmendStudyData }) =>
      studyService.amendStudy(id, data),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: studyKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: studyKeys.amendInfo(variables.id) })
      queryClient.invalidateQueries({ queryKey: studyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ['worklist'] })
    },
  })
}
